import { useState, useEffect, useCallback } from "react";
import {
  Radio, RefreshCw, Loader2, Trophy, Timer, Maximize2, Minimize2,
  ExternalLink, Facebook, Play, Pause, Volume2, VolumeX, Monitor
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import AdminLayout from "@/components/layout/AdminLayout";
import axios from "axios";
import { API } from "@/App";

const getImageUrl = (url) => {
  if (!url) return null;
  if (url.startsWith("http")) return url;
  const backendUrl = process.env.REACT_APP_BACKEND_URL || "";
  return url.startsWith("/uploads/") ? `${backendUrl}/api${url}` : `${backendUrl}${url}`;
};

// Helper function to extract Facebook video URL for embedding
const getFacebookEmbedUrl = (fbLink) => {
  if (!fbLink) return null;
  // Facebook video embed URL format
  // Encode the original URL for the embed
  const encodedUrl = encodeURIComponent(fbLink);
  return `https://www.facebook.com/plugins/video.php?href=${encodedUrl}&show_text=false&width=560&autoplay=true&mute=true`;
};

// Individual match card for the grid with embedded video
function MatchCard({ match, tournament }) {
  if (!match) {
    return (
      <div className="bg-gray-900 rounded-xl border border-gray-800 flex items-center justify-center h-full min-h-[350px]">
        <div className="text-center text-gray-600">
          <Monitor className="h-12 w-12 mx-auto mb-2 opacity-50" />
          <p className="text-sm">Sin transmisión</p>
        </div>
      </div>
    );
  }

  const embedUrl = getFacebookEmbedUrl(match.facebook_link);

  return (
    <div className={`bg-gray-900 rounded-xl border-2 overflow-hidden transition-all flex flex-col ${
      match.status === 'live' ? 'border-red-500/50 shadow-lg shadow-red-500/20' : 'border-gray-800'
    }`}>
      {/* Tournament Header */}
      <div 
        className="px-3 py-2 flex items-center justify-between flex-shrink-0"
        style={{ backgroundColor: tournament?.color || '#10B981' }}
      >
        <div className="flex items-center gap-2 text-white">
          <Trophy className="h-4 w-4" />
          <span className="text-sm font-medium truncate">{tournament?.name || 'Torneo'}</span>
        </div>
        {match.status === 'live' && (
          <Badge className="bg-red-500 text-white text-xs animate-pulse">
            <Radio className="h-3 w-3 mr-1" />
            EN VIVO
          </Badge>
        )}
      </div>

      {/* Teams and Score Bar */}
      <div className="px-3 py-2 bg-gray-800 flex items-center justify-between flex-shrink-0">
        {/* Home Team */}
        <div className="flex items-center gap-2 flex-1">
          {match.home_team?.logo_url && (
            <img 
              src={getImageUrl(match.home_team.logo_url)} 
              alt="" 
              className="w-8 h-8 object-contain"
            />
          )}
          <span className="text-white text-sm font-medium truncate">{match.home_team?.name}</span>
        </div>

        {/* Score */}
        <div className="flex items-center gap-2 px-3">
          <span className="text-2xl font-black text-white">
            {match.home_score ?? 0} - {match.away_score ?? 0}
          </span>
          {match.status === 'live' && match.minute && (
            <div className="flex items-center gap-1">
              <Timer className="h-3 w-3 text-red-500" />
              <span className="text-red-500 text-sm font-bold">{match.minute}'</span>
            </div>
          )}
        </div>

        {/* Away Team */}
        <div className="flex items-center gap-2 flex-1 justify-end">
          <span className="text-white text-sm font-medium truncate">{match.away_team?.name}</span>
          {match.away_team?.logo_url && (
            <img 
              src={getImageUrl(match.away_team.logo_url)} 
              alt="" 
              className="w-8 h-8 object-contain"
            />
          )}
        </div>
      </div>

      {/* Facebook Video Embed Area */}
      <div className="flex-1 bg-black relative min-h-[200px]">
        {embedUrl ? (
          <iframe
            src={embedUrl}
            className="absolute inset-0 w-full h-full"
            style={{ border: 'none', overflow: 'hidden' }}
            scrolling="no"
            frameBorder="0"
            allowFullScreen={true}
            allow="autoplay; clipboard-write; encrypted-media; picture-in-picture; web-share"
            title={`${match.home_team?.name} vs ${match.away_team?.name}`}
          />
        ) : (
          <div className="absolute inset-0 flex flex-col items-center justify-center text-gray-500">
            <Facebook className="h-12 w-12 mb-2 opacity-30" />
            <p className="text-sm">Sin enlace de transmisión</p>
            <p className="text-xs mt-1 opacity-60">Agrega el enlace de Facebook en Partidos</p>
          </div>
        )}
      </div>

      {/* Footer with external link */}
      <div className="px-3 py-2 bg-gray-800/80 flex items-center justify-between flex-shrink-0">
        <span className="text-xs text-gray-400 truncate">{match.stadium || 'Estadio'}</span>
        {match.facebook_link && (
          <a
            href={match.facebook_link}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-1 text-xs text-blue-400 hover:text-blue-300"
          >
            <ExternalLink className="h-3 w-3" />
            Abrir en Facebook
          </a>
        )}
      </div>
    </div>
  );
}

export default function AdminTransmisiones() {
  const [matches, setMatches] = useState([]);
  const [tournaments, setTournaments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [lastUpdate, setLastUpdate] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      const [matchesRes, tournamentsRes] = await Promise.all([
        axios.get(`${API}/sports/matches?status=live&limit=100`),
        axios.get(`${API}/sports/tournaments`),
      ]);
      
      setMatches(matchesRes.data.items || []);
      setTournaments(tournamentsRes.data.items || []);
      setLastUpdate(new Date());
    } catch (error) {
      console.error("Error fetching data:", error);
      toast.error("Error al cargar datos");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Auto-refresh every 30 seconds
  useEffect(() => {
    if (!autoRefresh) return;
    
    const interval = setInterval(() => {
      fetchData();
    }, 30000);

    return () => clearInterval(interval);
  }, [autoRefresh, fetchData]);

  const getTournamentById = (id) => tournaments.find(t => t.tournament_id === id);

  // Fill array to always have 9 slots (3x3 grid)
  const gridMatches = [...matches];
  while (gridMatches.length < 9) {
    gridMatches.push(null);
  }

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-brand-text flex items-center gap-3">
              <div className="relative">
                <Monitor className="h-7 w-7 text-primary" />
                {matches.length > 0 && (
                  <span className="absolute -top-1 -right-1 h-3 w-3 bg-red-500 rounded-full animate-pulse" />
                )}
              </div>
              Centro de Transmisiones
            </h1>
            <p className="text-brand-textSecondary">
              Monitorea todos los partidos en vivo en una vista
            </p>
          </div>

          <div className="flex items-center gap-3">
            {/* Last Update */}
            {lastUpdate && (
              <span className="text-xs text-gray-500">
                Actualizado: {lastUpdate.toLocaleTimeString('es-HN')}
              </span>
            )}

            {/* Auto Refresh Toggle */}
            <Button
              variant={autoRefresh ? "default" : "outline"}
              size="sm"
              onClick={() => setAutoRefresh(!autoRefresh)}
              className="gap-2"
            >
              {autoRefresh ? (
                <>
                  <Pause className="h-4 w-4" />
                  Auto (30s)
                </>
              ) : (
                <>
                  <Play className="h-4 w-4" />
                  Manual
                </>
              )}
            </Button>

            {/* Manual Refresh */}
            <Button
              variant="outline"
              size="sm"
              onClick={fetchData}
              disabled={loading}
              className="gap-2"
            >
              <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
              Actualizar
            </Button>
          </div>
        </div>

        {/* Stats Bar */}
        <div className="flex flex-wrap items-center gap-4 p-4 bg-gray-100 rounded-xl">
          <div className="flex items-center gap-2">
            <div className="h-3 w-3 bg-red-500 rounded-full animate-pulse" />
            <span className="font-bold text-lg">{matches.length}</span>
            <span className="text-gray-600">Partidos en vivo</span>
          </div>
          <div className="h-6 w-px bg-gray-300" />
          <div className="flex items-center gap-2">
            <Facebook className="h-4 w-4 text-blue-600" />
            <span className="font-bold">{matches.filter(m => m.facebook_link).length}</span>
            <span className="text-gray-600">Con transmisión</span>
          </div>
        </div>

        {/* Main Grid - 3x3 */}
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="h-12 w-12 animate-spin text-primary" />
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {gridMatches.slice(0, 9).map((match, index) => (
              <div key={match?.match_id || `empty-${index}`} className="h-[380px]">
                <MatchCard
                  match={match}
                  tournament={match ? getTournamentById(match.tournament_id) : null}
                />
              </div>
            ))}
          </div>
        )}

        {/* Additional matches if more than 9 */}
        {matches.length > 9 && (
          <div className="mt-6">
            <h3 className="text-lg font-bold text-gray-700 mb-4 flex items-center gap-2">
              <Radio className="h-5 w-5 text-red-500" />
              Más partidos en vivo ({matches.length - 9})
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {matches.slice(9).map((match) => (
                <div key={match.match_id} className="h-[380px]">
                  <MatchCard
                    match={match}
                    tournament={getTournamentById(match.tournament_id)}
                  />
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Empty State */}
        {!loading && matches.length === 0 && (
          <div className="text-center py-16 bg-gray-900 rounded-2xl">
            <Monitor className="h-20 w-20 text-gray-700 mx-auto mb-4" />
            <h3 className="text-xl font-bold text-gray-400 mb-2">No hay partidos en vivo</h3>
            <p className="text-gray-600 max-w-md mx-auto">
              Cuando los partidos estén marcados como "En Vivo" aparecerán aquí automáticamente.
              La página se actualiza cada 30 segundos.
            </p>
          </div>
        )}

        {/* Tips */}
        <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
          <h4 className="font-medium text-blue-800 mb-2">Tips para el monitoreo:</h4>
          <ul className="text-sm text-blue-700 space-y-1">
            <li>• La página se actualiza automáticamente cada 30 segundos</li>
            <li>• Los partidos con transmisión de Facebook muestran el botón azul</li>
            <li>• Usa la pestaña "En Vivo" en Partidos para editar marcadores rápidamente</li>
            <li>• Puedes desactivar la actualización automática con el botón "Manual"</li>
          </ul>
        </div>
      </div>
    </AdminLayout>
  );
}
